// BusinessHoursPage - Nyitvatartás kezelő admin oldal (Epic 57, Story 57-5)
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import {
  useBusinessHours,
  useCalendarMutations,
  useHolidays,
  useOverrides,
} from '@/hooks/use-calendar';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================
// CONSTANTS
// ============================================

const DAY_KEYS: Record<number, string> = {
  0: 'businessHours.dayNames.monday',
  1: 'businessHours.dayNames.tuesday',
  2: 'businessHours.dayNames.wednesday',
  3: 'businessHours.dayNames.thursday',
  4: 'businessHours.dayNames.friday',
  5: 'businessHours.dayNames.saturday',
  6: 'businessHours.dayNames.sunday',
};

type BusinessHoursTab = 'weekly' | 'holidays' | 'overrides';

const TAB_KEYS: { id: BusinessHoursTab; labelKey: string }[] = [
  { id: 'weekly', labelKey: 'businessHours.tabWeekly' },
  { id: 'holidays', labelKey: 'businessHours.tabHolidays' },
  { id: 'overrides', labelKey: 'businessHours.tabOverrides' },
];

// ============================================
// WEEKLY SCHEDULE TAB
// ============================================

function WeeklyScheduleTab() {
  const { t } = useTranslation();
  const { businessHours, isLoading, error, refetch } = useBusinessHours();
  const mutations = useCalendarMutations();

  // Local edit state: map dayOfWeek -> { isOpen, openTime, closeTime }
  const [editState, setEditState] = useState<
    Record<number, { isOpen: boolean; openTime: string; closeTime: string }>
  >({});
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const getDayState = useCallback(
    (dayOfWeek: number) => {
      const override = editState[dayOfWeek];
      if (override) return override;
      const found = businessHours.find(bh => bh.dayOfWeek === dayOfWeek);
      return {
        isOpen: found?.isOpen ?? false,
        openTime: found?.openTime ?? '08:00',
        closeTime: found?.closeTime ?? '17:00',
      };
    },
    [businessHours, editState]
  );

  const updateLocalDay = useCallback(
    (
      dayOfWeek: number,
      patch: Partial<{ isOpen: boolean; openTime: string; closeTime: string }>
    ) => {
      setEditState(prev => ({
        ...prev,
        [dayOfWeek]: {
          ...getDayState(dayOfWeek),
          ...patch,
        },
      }));
    },
    [getDayState]
  );

  const handleSaveAll = useCallback(async () => {
    setSaveSuccess(null);
    try {
      // Save all 7 days - send current state for each
      const promises = Array.from({ length: 7 }, (_, i) => {
        const state = getDayState(i);
        return mutations.updateDay(i, {
          isOpen: state.isOpen,
          openTime: state.openTime,
          closeTime: state.closeTime,
        });
      });
      await Promise.all(promises);
      setEditState({});
      await refetch();
      setSaveSuccess(t('businessHours.savingSuccess'));
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch {
      // Error is handled by the mutation hook
    }
  }, [getDayState, mutations, refetch, t]);

  const handleReset = useCallback(async () => {
    setSaveSuccess(null);
    try {
      await mutations.resetDefaults();
      setEditState({});
      await refetch();
      setSaveSuccess(t('businessHours.resetSuccess'));
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch {
      // Error is handled by the mutation hook
    }
  }, [mutations, refetch, t]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">{t('businessHours.loadingWeekly')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('businessHours.weeklyScheduleTitle')}</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset} disabled={mutations.isLoading}>
              {t('businessHours.defaultButton')}
            </Button>
            <Button onClick={handleSaveAll} disabled={mutations.isLoading}>
              {mutations.isLoading
                ? t('businessHours.savingButton')
                : t('businessHours.saveButton')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {(error ?? mutations.error) && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
            {error ?? mutations.error}
          </div>
        )}
        {saveSuccess && (
          <div className="mb-4 rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-600 dark:text-green-400">
            {saveSuccess}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-slate-600">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('businessHours.dayColumn')}
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('businessHours.openColumn')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('businessHours.openingColumn')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('businessHours.closingColumn')}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 7 }, (_, dayOfWeek) => {
                const state = getDayState(dayOfWeek);
                const isWeekend = dayOfWeek >= 5;
                return (
                  <tr
                    key={dayOfWeek}
                    className={cn(
                      'border-b dark:border-slate-700',
                      isWeekend && 'bg-gray-50 dark:bg-slate-800/50'
                    )}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'font-medium',
                          state.isOpen
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-400 dark:text-gray-300'
                        )}
                      >
                        {t(DAY_KEYS[dayOfWeek] ?? `businessHours.dayNames.monday`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={state.isOpen}
                        onClick={() => updateLocalDay(dayOfWeek, { isOpen: !state.isOpen })}
                        className={cn(
                          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                          state.isOpen ? 'bg-primary' : 'bg-gray-200 dark:bg-slate-600'
                        )}
                      >
                        <span
                          className={cn(
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                            state.isOpen ? 'translate-x-5' : 'translate-x-0'
                          )}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="time"
                        value={state.openTime}
                        onChange={e => updateLocalDay(dayOfWeek, { openTime: e.target.value })}
                        disabled={!state.isOpen}
                        className={cn(
                          'flex h-9 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
                          !state.isOpen && 'opacity-50 cursor-not-allowed'
                        )}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="time"
                        value={state.closeTime}
                        onChange={e => updateLocalDay(dayOfWeek, { closeTime: e.target.value })}
                        disabled={!state.isOpen}
                        className={cn(
                          'flex h-9 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
                          !state.isOpen && 'opacity-50 cursor-not-allowed'
                        )}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// HOLIDAYS TAB
// ============================================

interface HolidayFormState {
  name: string;
  date: string;
  isRecurring: boolean;
}

const EMPTY_HOLIDAY_FORM: HolidayFormState = {
  name: '',
  date: '',
  isRecurring: false,
};

function HolidaysTab() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const { holidays, isLoading, error, refetch } = useHolidays(selectedYear);
  const mutations = useCalendarMutations();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<HolidayFormState>(EMPTY_HOLIDAY_FORM);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_HOLIDAY_FORM);
    setShowForm(true);
  }, []);

  const handleOpenEdit = useCallback(
    (id: string) => {
      const holiday = holidays.find(h => h.id === id);
      if (!holiday) return;
      setEditingId(id);
      setForm({
        name: holiday.name,
        date: holiday.date.substring(0, 10), // ISO date -> YYYY-MM-DD
        isRecurring: holiday.isRecurring,
      });
      setShowForm(true);
    },
    [holidays]
  );

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_HOLIDAY_FORM);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim() || !form.date) return;
    try {
      if (editingId) {
        await mutations.updateHoliday(editingId, {
          name: form.name.trim(),
          date: form.date,
          isRecurring: form.isRecurring,
        });
        showSuccess(t('businessHours.holidayUpdated'));
      } else {
        await mutations.createHoliday({
          name: form.name.trim(),
          date: form.date,
          isRecurring: form.isRecurring,
        });
        showSuccess(t('businessHours.holidayCreated'));
      }
      handleCancel();
      await refetch();
    } catch {
      // Error is handled by the mutation hook
    }
  }, [form, editingId, mutations, handleCancel, refetch, showSuccess, t]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await mutations.deleteHoliday(id);
        showSuccess(t('businessHours.holidayDeleted'));
        await refetch();
      } catch {
        // Error is handled by the mutation hook
      }
    },
    [mutations, refetch, showSuccess, t]
  );

  const handleSeedNational = useCallback(async () => {
    try {
      const result = await mutations.seedNational(selectedYear);
      showSuccess(`${result.seeded} ${t('businessHours.nationalSeeded')}`);
      await refetch();
    } catch {
      // Error is handled by the mutation hook
    }
  }, [mutations, selectedYear, refetch, showSuccess, t]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">{t('businessHours.loadingHolidays')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('businessHours.holidaysTitle')}</CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="flex h-10 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {Array.from({ length: 5 }, (_, i) => currentYear - 1 + i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <Button variant="outline" onClick={handleSeedNational} disabled={mutations.isLoading}>
                {t('businessHours.hungHolidays')}
              </Button>
              <Button onClick={handleOpenCreate} disabled={mutations.isLoading}>
                {t('businessHours.newHolidayButton')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(error ?? mutations.error) && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
              {error ?? mutations.error}
            </div>
          )}
          {successMessage && (
            <div className="mb-4 rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-600 dark:text-green-400">
              {successMessage}
            </div>
          )}

          {/* Inline form */}
          {showForm && (
            <div className="mb-6 rounded-lg border border-gray-200 dark:border-slate-600 p-4 bg-gray-50 dark:bg-slate-800/50">
              <h4 className="mb-3 font-medium text-gray-900 dark:text-gray-100">
                {editingId
                  ? t('businessHours.editHolidayTitle')
                  : t('businessHours.newHolidayTitle')}
              </h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t('businessHours.nameLabel')}
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('businessHours.namePlaceholder')}
                />
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('businessHours.dateLabel')}
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={e => setForm(prev => ({ ...prev, isRecurring: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('businessHours.recurringLabel')}
                    </span>
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  {t('businessHours.cancelButton')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={mutations.isLoading || !form.name.trim() || !form.date}
                >
                  {mutations.isLoading
                    ? t('businessHours.savingButton')
                    : editingId
                      ? t('businessHours.updateButton')
                      : t('businessHours.createButton')}
                </Button>
              </div>
            </div>
          )}

          {/* Holiday list */}
          {holidays.length === 0 ? (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">
              {t('businessHours.noHolidaysText')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b dark:border-slate-600">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('businessHours.nameColumn')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('businessHours.dateColumnHeader')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('businessHours.typeColumn')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('businessHours.actionsColumn')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map(holiday => (
                    <tr key={holiday.id} className="border-b dark:border-slate-700">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {holiday.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {formatDate(holiday.date)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {holiday.isNational && (
                            <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-300">
                              {t('businessHours.nationalBadge')}
                            </span>
                          )}
                          {holiday.isRecurring && (
                            <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/30 px-2.5 py-0.5 text-xs font-medium text-purple-800 dark:text-purple-300">
                              {t('businessHours.recurringBadge')}
                            </span>
                          )}
                          {!holiday.isNational && !holiday.isRecurring && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                              {t('businessHours.customBadge')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(holiday.id)}
                            className="text-sm text-primary hover:text-primary/80"
                            disabled={mutations.isLoading}
                          >
                            {t('businessHours.editButton')}
                          </button>
                          <button
                            onClick={() => handleDelete(holiday.id)}
                            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            disabled={mutations.isLoading}
                          >
                            {t('businessHours.deleteButton')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// OVERRIDES TAB
// ============================================

interface OverrideFormState {
  date: string;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  reason: string;
}

const EMPTY_OVERRIDE_FORM: OverrideFormState = {
  date: '',
  isOpen: false,
  openTime: '08:00',
  closeTime: '17:00',
  reason: '',
};

function OverridesTab() {
  const { t } = useTranslation();
  const { overrides, isLoading, error, refetch } = useOverrides();
  const mutations = useCalendarMutations();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<OverrideFormState>(EMPTY_OVERRIDE_FORM);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_OVERRIDE_FORM);
    setShowForm(true);
  }, []);

  const handleOpenEdit = useCallback(
    (id: string) => {
      const override = overrides.find(o => o.id === id);
      if (!override) return;
      setEditingId(id);
      setForm({
        date: override.date.substring(0, 10),
        isOpen: override.isOpen,
        openTime: override.openTime ?? '08:00',
        closeTime: override.closeTime ?? '17:00',
        reason: override.reason ?? '',
      });
      setShowForm(true);
    },
    [overrides]
  );

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_OVERRIDE_FORM);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.date) return;
    try {
      const payload = {
        isOpen: form.isOpen,
        openTime: form.isOpen ? form.openTime : null,
        closeTime: form.isOpen ? form.closeTime : null,
        reason: form.reason.trim() || null,
      };
      if (editingId) {
        await mutations.updateOverride(editingId, payload);
        showSuccess(t('businessHours.overrideUpdated'));
      } else {
        await mutations.createOverride({ date: form.date, ...payload });
        showSuccess(t('businessHours.overrideCreated'));
      }
      handleCancel();
      await refetch();
    } catch {
      // Error is handled by the mutation hook
    }
  }, [form, editingId, mutations, handleCancel, refetch, showSuccess, t]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await mutations.deleteOverride(id);
        showSuccess(t('businessHours.overrideDeleted'));
        await refetch();
      } catch {
        // Error is handled by the mutation hook
      }
    },
    [mutations, refetch, showSuccess, t]
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">{t('businessHours.loadingOverrides')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('businessHours.overridesTitle')}</CardTitle>
          <Button onClick={handleOpenCreate} disabled={mutations.isLoading}>
            {t('businessHours.newOverrideButton')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {(error ?? mutations.error) && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
            {error ?? mutations.error}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-600 dark:text-green-400">
            {successMessage}
          </div>
        )}

        {/* Inline form */}
        {showForm && (
          <div className="mb-6 rounded-lg border border-gray-200 dark:border-slate-600 p-4 bg-gray-50 dark:bg-slate-800/50">
            <h4 className="mb-3 font-medium text-gray-900 dark:text-gray-100">
              {editingId
                ? t('businessHours.editOverrideTitle')
                : t('businessHours.newOverrideTitle')}
            </h4>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('businessHours.dateLabel')}
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                  disabled={editingId !== null}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
                    editingId !== null && 'opacity-50 cursor-not-allowed'
                  )}
                />
              </div>
              <div className="flex items-end gap-4 pb-0.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isOpen}
                    onChange={e => setForm(prev => ({ ...prev, isOpen: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('businessHours.open')}
                  </span>
                </label>
              </div>
              {form.isOpen && (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('businessHours.openingColumn')}
                    </label>
                    <input
                      type="time"
                      value={form.openTime}
                      onChange={e => setForm(prev => ({ ...prev, openTime: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('businessHours.closingColumn')}
                    </label>
                    <input
                      type="time"
                      value={form.closeTime}
                      onChange={e => setForm(prev => ({ ...prev, closeTime: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="mt-4">
              <Input
                label={t('businessHours.reasonLabel')}
                value={form.reason}
                onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={t('businessHours.reasonPlaceholder')}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>
                {t('businessHours.cancelButton')}
              </Button>
              <Button onClick={handleSubmit} disabled={mutations.isLoading || !form.date}>
                {mutations.isLoading
                  ? t('businessHours.savingButton')
                  : editingId
                    ? t('businessHours.updateButton')
                    : t('businessHours.createButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Override list */}
        {overrides.length === 0 ? (
          <p className="py-8 text-center text-gray-500 dark:text-gray-400">
            {t('businessHours.noOverridesText')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b dark:border-slate-600">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('businessHours.dateColumnHeader')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('businessHours.statusColumn')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('businessHours.timeColumn')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('businessHours.reasonColumn')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('businessHours.actionsColumn')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {overrides.map(override => (
                  <tr key={override.id} className="border-b dark:border-slate-700">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(override.date)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {override.isOpen ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
                          {t('businessHours.openBadge')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                          {t('businessHours.closedBadge')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {override.isOpen && override.openTime && override.closeTime
                        ? `${override.openTime} - ${override.closeTime}`
                        : '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {override.reason ?? '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(override.id)}
                          className="text-sm text-primary hover:text-primary/80"
                          disabled={mutations.isLoading}
                        >
                          {t('businessHours.editButton')}
                        </button>
                        <button
                          onClick={() => handleDelete(override.id)}
                          className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          disabled={mutations.isLoading}
                        >
                          {t('businessHours.deleteButton')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// HELPERS
// ============================================

function formatDate(isoDate: string): string {
  const dateStr = isoDate.substring(0, 10);
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  return `${parts[0]}.${parts[1]}.${parts[2]}.`;
}

// ============================================
// MAIN PAGE
// ============================================

export function BusinessHoursPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BusinessHoursTab>('weekly');

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('businessHours.pageTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('businessHours.pageSubtitle')}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Tab navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-slate-600">
            <nav className="-mb-px flex gap-6" aria-label="Tabs">
              {TAB_KEYS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-slate-500 hover:text-gray-700 dark:hover:text-gray-300'
                  )}
                >
                  {t(tab.labelKey)}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'weekly' && <WeeklyScheduleTab />}
        {activeTab === 'holidays' && <HolidaysTab />}
        {activeTab === 'overrides' && <OverridesTab />}
      </main>
    </div>
  );
}
